import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { AccessibilityInfo } from 'react-native';

import { NutritionCalculator } from '@sg/garnish';
import type { MixedDressingDetails } from '@sg/graphql-schema';
import {
  checkIfDressingIngredient,
  type CustomizationMachineTypes,
  getDressingPortionSize,
} from '@sg/shared-fe-features-customization';
import { useProductDynamicMicroNutritionData } from '@sg/shared-fe-features-menu';

import { useTelemetry } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

const Floating = (props: FloatingProps) => {
  const {
    calories,
    activeModifications,
    mixedDressingDetails,
    product,
    variant = 'standard',
  } = props;

  const { formatMessage } = useIntl();
  const { trackEvent } = useTelemetry();

  // ─── State ───────────────────────────────────────────────────────────

  const [isShown, setIsShown] = useState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const toggleOpen = useCallback(() => {
    setIsShown(!isShown);

    trackEvent({
      name: isShown
        ? 'customization.macro-calculator.closed'
        : 'customization.macro-calculator.opened',
      payload: { productId: product.id, productName: product.name },
    });
  }, [isShown, product.id, product.name, trackEvent]);

  // ─────────────────────────────────────────────────────────────────────

  const toggleA11yLabel = isShown
    ? formatMessage(messages.hideNutritionCalculator)
    : formatMessage(messages.showNutritionCalculator);

  const dialogA11yLabel = formatMessage(messages.nutritionCalculator);
  const dialogOpenAnnouncement = formatMessage(messages.nutritionCalculatorOpened); // prettier-ignore

  // ─── Nutrition Data ──────────────────────────────────────────────────

  const ingredients = useMemo(
    () => convertToIngredients(activeModifications, mixedDressingDetails),
    [activeModifications, mixedDressingDetails],
  );

  const macroNutritionData = useProductDynamicMicroNutritionData({
    calories,
    ingredients,
    useFallbackValues: true,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isShown) return;

    AccessibilityInfo.announceForAccessibility(dialogOpenAnnouncement);
  }, [dialogOpenAnnouncement, isShown]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <NutritionCalculator.Floating.Container>
      <NutritionCalculator.Floating.Dialog
        isShown={isShown}
        onTogglePress={toggleOpen}
        toggleA11yLabel={toggleA11yLabel}
        dialogA11yLabel={dialogA11yLabel}
        variant={variant}
      >
        <NutritionCalculator.List>
          {macroNutritionData.map((entry) => (
            <NutritionCalculator.Item
              key={entry.label}
              label={entry.label}
              value={entry.value}
              direction="vertical"
            />
          ))}
        </NutritionCalculator.List>
      </NutritionCalculator.Floating.Dialog>
    </NutritionCalculator.Floating.Container>
  );
};

const Static = (props: Props) => {
  const { calories, activeModifications, mixedDressingDetails } = props;

  // ─── Nutrition Data ──────────────────────────────────────────────────

  const ingredients = useMemo(
    () => convertToIngredients(activeModifications, mixedDressingDetails),
    [activeModifications, mixedDressingDetails],
  );

  const macroNutritionData = useProductDynamicMicroNutritionData({
    calories,
    ingredients,
    useFallbackValues: true,
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <NutritionCalculator.Static>
      <NutritionCalculator.List withoutSpacing>
        {macroNutritionData.map((entry) => (
          <NutritionCalculator.Item
            key={entry.label}
            label={entry.label}
            value={entry.value}
            direction="horizontal"
          />
        ))}
      </NutritionCalculator.List>
    </NutritionCalculator.Static>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  nutritionCalculator: {
    defaultMessage: 'Nutrition calculator popover',
    description: 'Customization | Nutrition calculator | Dialog a11y label',
  },
  nutritionCalculatorOpened: {
    defaultMessage: 'Nutrition calculator dialog opened',
    description: 'Customization | Nutrition calculator | Dialog shown message',
  },
  showNutritionCalculator: {
    defaultMessage: 'Show nutrition calculator',
    description: 'Customization | Nutrition calculator | Show nutrition calculator', // prettier-ignore
  },
  hideNutritionCalculator: {
    defaultMessage: 'Hide nutrition calculator',
    description: 'Customization | Nutrition calculator | Hide nutrition calculator', // prettier-ignore
  },
});

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationNutritionCalculator = {
  Floating,
  Static,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function convertToIngredients(
  modifications: ActiveModifications,
  mixedDressingDetails: MixedDressingDetails[],
): Ingredients {
  return modifications
    .map((modification) => ({
      ...modification.ingredient,
      quantity: checkIfDressingIngredient(modification)
        ? getDressingPortionSize(modification, mixedDressingDetails)
        : modification.quantity,
    }))
    .filter(Boolean) as Ingredients;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  calories: number;
  activeModifications: ActiveModifications;
  mixedDressingDetails: MixedDressingDetails[];

  // eslint-disable-next-line react/no-unused-prop-types
  product: Pick<CustomizationMachineTypes.Product, 'id' | 'name'>;
};

type FloatingProps = Props & {
  variant?: 'compact' | 'standard';
};

type ActiveModifications = CustomizationMachineTypes.Modifications['active'];
type Ingredients = Parameters<typeof useProductDynamicMicroNutritionData>[0]['ingredients'];
